import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { apiGetLocations } from '../services/api';

// Leaflet is loaded from a CDN on demand (pinned version + Subresource
// Integrity) so the dashboard gains an interactive map without adding a build
// dependency. The SRI hashes are the official ones published for Leaflet 1.9.4.
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
const LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';

let leafletPromise = null;
const loadLeaflet = () => {
  if (window.L) return Promise.resolve(window.L);
  if (leafletPromise) return leafletPromise;

  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-leaflet]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.integrity = LEAFLET_CSS_SRI;
      link.crossOrigin = '';
      link.setAttribute('data-leaflet', '1');
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.integrity = LEAFLET_JS_SRI;
    script.crossOrigin = '';
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load Leaflet'));
    document.head.appendChild(script);
  });
  return leafletPromise;
};

// Parses the JSON-Lines breadcrumb payload into an array of valid points.
const parseLocations = (text) => {
  if (!text || typeof text !== 'string') return [];
  const points = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    try {
      const obj = JSON.parse(line);
      const lat = Number(obj.lat);
      const lon = Number(obj.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        points.push({ t: obj.t || '', lat, lon, alt: Number.isFinite(Number(obj.alt)) ? Number(obj.alt) : undefined });
      }
    } catch {
      // Skip a malformed line (e.g. a half-written tail during a live upload).
    }
  }
  return points;
};

const POLL_INTERVAL_MS = 12000;

const LocationMap = ({ selectedFile, currentTime = 0 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const leafletRef = useRef(null);
  const playheadRef = useRef(null);
  const currentTimeRef = useRef(0);

  const [points, setPoints] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | empty | error

  // Map each breadcrumb to elapsed seconds from the first fix, so the live
  // playback time can be matched to a position along the track. Breadcrumbs are
  // stamped with the capture UTC (ISO-8601), and audio playback time is measured
  // from the start of the recording, so anchoring to the first fix aligns them.
  const elapsed = useMemo(() => {
    const secs = points.map((p) => {
      const ms = Date.parse(p.t);
      return Number.isFinite(ms) ? ms / 1000 : NaN;
    });
    const base = secs.find((s) => Number.isFinite(s));
    if (base === undefined) return [];
    return secs.map((s) => (Number.isFinite(s) ? s - base : NaN));
  }, [points]);

  // Interpolates the [lat, lon] the recorder was at for a given playback second.
  const positionAt = useCallback((t) => {
    if (points.length === 0) return null;
    const seq = [];
    for (let i = 0; i < points.length; i += 1) {
      if (Number.isFinite(elapsed[i])) seq.push({ e: elapsed[i], p: points[i] });
    }
    if (seq.length === 0) return [points[0].lat, points[0].lon];
    if (t <= seq[0].e) return [seq[0].p.lat, seq[0].p.lon];
    const last = seq[seq.length - 1];
    if (t >= last.e) return [last.p.lat, last.p.lon];
    for (let i = 0; i < seq.length - 1; i += 1) {
      const a = seq[i];
      const b = seq[i + 1];
      if (t >= a.e && t <= b.e) {
        const span = b.e - a.e;
        const frac = span > 0 ? (t - a.e) / span : 0;
        return [
          a.p.lat + (b.p.lat - a.p.lat) * frac,
          a.p.lon + (b.p.lon - a.p.lon) * frac,
        ];
      }
    }
    return [last.p.lat, last.p.lon];
  }, [points, elapsed]);

  // Fetch (and poll) the breadcrumb track for the selected file.
  useEffect(() => {
    if (!selectedFile) {
      setPoints([]);
      setStatus('empty');
      return;
    }

    let cancelled = false;
    let timer = null;

    const fetchOnce = async () => {
      try {
        const res = await apiGetLocations(selectedFile);
        if (cancelled) return;
        if (res && res.status === 200) {
          const parsed = parseLocations(res.data);
          setPoints(parsed);
          setStatus(parsed.length > 0 ? 'ready' : 'empty');
        } else {
          setPoints([]);
          setStatus('empty');
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 404 || code === 204) {
          setPoints([]);
          setStatus('empty');
        } else {
          setStatus('error');
        }
      }
    };

    setStatus('loading');
    fetchOnce();
    // Live updates: keep pulling so an in-progress recording's track grows.
    timer = setInterval(fetchOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [selectedFile]);

  // Create the Leaflet map once the container is mounted and there is data.
  useEffect(() => {
    if (status !== 'ready' || points.length === 0) return;

    let disposed = false;

    loadLeaflet()
      .then((L) => {
        if (disposed || !containerRef.current) return;
        leafletRef.current = L;

        if (!mapRef.current) {
          mapRef.current = L.map(containerRef.current, { scrollWheelZoom: false });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
            maxZoom: 19,
          }).addTo(mapRef.current);
        }

        const map = mapRef.current;

        // Rebuild the track layer from scratch each refresh.
        if (layerRef.current) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        const group = L.layerGroup();
        const latlngs = points.map((p) => [p.lat, p.lon]);

        L.polyline(latlngs, { color: '#2563eb', weight: 3, opacity: 0.85 }).addTo(group);

        points.forEach((p, i) => {
          const isFirst = i === 0;
          const isLast = i === points.length - 1;
          const color = isFirst ? '#16a34a' : isLast ? '#dc2626' : '#2563eb';
          const radius = isFirst || isLast ? 7 : 4;
          const marker = L.circleMarker([p.lat, p.lon], {
            radius,
            color,
            weight: 2,
            fillColor: color,
            fillOpacity: 0.85,
          });
          const label = isFirst ? 'Start' : isLast ? 'End' : 'Waypoint';
          const alt = p.alt !== undefined ? `<br/>Alt: ${p.alt.toFixed(1)} m` : '';
          marker.bindPopup(
            `<strong>${label}</strong><br/>${p.t || ''}<br/>${p.lat.toFixed(6)}, ${p.lon.toFixed(6)}${alt}`
          );
          marker.addTo(group);
        });

        group.addTo(map);
        layerRef.current = group;

        if (latlngs.length === 1) {
          map.setView(latlngs[0], 15);
        } else {
          map.fitBounds(L.latLngBounds(latlngs), { padding: [24, 24], maxZoom: 16 });
        }

        // (Re)create the playback playhead marker on top of the track so the
        // viewer can see where the recorder was at the current audio position.
        if (playheadRef.current) {
          map.removeLayer(playheadRef.current);
          playheadRef.current = null;
        }
        const startPos = positionAt(currentTimeRef.current);
        if (startPos) {
          playheadRef.current = L.circleMarker(startPos, {
            radius: 9,
            color: '#ffffff',
            weight: 3,
            fillColor: '#f59e0b',
            fillOpacity: 1,
          });
          playheadRef.current.bindTooltip('Playback position', { direction: 'top', offset: [0, -8] });
          playheadRef.current.addTo(map);
          playheadRef.current.bringToFront();
        }

        // The container may have been sized after creation; force a recalculation.
        setTimeout(() => { if (!disposed && mapRef.current) mapRef.current.invalidateSize(); }, 0);
      })
      .catch(() => setStatus('error'));

    return () => {
      disposed = true;
    };
  }, [status, points, positionAt]);

  // Move the playhead as the audio plays, without rebuilding the track layer.
  useEffect(() => {
    currentTimeRef.current = currentTime;
    const L = leafletRef.current;
    const map = mapRef.current;
    if (!L || !map || status !== 'ready') return;
    const pos = positionAt(currentTime);
    if (!pos) return;
    if (!playheadRef.current) {
      playheadRef.current = L.circleMarker(pos, {
        radius: 9,
        color: '#ffffff',
        weight: 3,
        fillColor: '#f59e0b',
        fillOpacity: 1,
      });
      playheadRef.current.bindTooltip('Playback position', { direction: 'top', offset: [0, -8] });
      playheadRef.current.addTo(map);
    } else {
      playheadRef.current.setLatLng(pos);
    }
    playheadRef.current.bringToFront();
  }, [currentTime, status, positionAt]);

  // The Streams panel is kept mounted but hidden (display:none) while another
  // tab is active, so this map's container collapses to 0x0 and Leaflet caches
  // that stale size. On returning to the tab (or any resize) the container grows
  // back to its real size; without recomputing, the tiles render misaligned or
  // grey. A ResizeObserver re-syncs the map to the live container size whenever
  // it changes to a non-zero box.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (mapRef.current && el.clientWidth > 0 && el.clientHeight > 0) {
        mapRef.current.invalidateSize();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Tear down the map when the component unmounts.
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layerRef.current = null;
        playheadRef.current = null;
      }
    };
  }, []);

  const headerStyle = {
    fontSize: '0.7rem',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '10px',
  };

  return (
    <div>
      <div style={headerStyle}>📍 Recording location</div>

      {status === 'loading' && (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>Loading location track…</p>
      )}
      {status === 'empty' && (
        <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
          No location data available for this recording.
        </p>
      )}
      {status === 'error' && (
        <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: 0 }}>Failed to load location track.</p>
      )}

      <div
        ref={containerRef}
        style={{
          height: status === 'ready' ? '320px' : '0px',
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden',
          border: status === 'ready' ? '1px solid #e5e7eb' : 'none',
        }}
      />

      {status === 'ready' && (
        <p style={{ color: '#9ca3af', fontSize: '0.75rem', margin: '8px 0 0' }}>
          {points.length} breadcrumb{points.length === 1 ? '' : 's'} · orange marker follows playback · updates live while recording
        </p>
      )}
    </div>
  );
};

export default LocationMap;
